import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/20/solid'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import {
  IconButton,
  SkeletonCircle,
  SkeletonText,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  classNames,
} from '@sushiswap/ui'
import { Badge } from '@sushiswap/ui'
import { Currency } from '@sushiswap/ui'
import type React from 'react'
import { type CSSProperties, type FC, memo, useCallback } from 'react'
import { type Amount, type Fraction, ZERO, getChainById } from 'sushi'
import {
  type EvmAddress,
  type EvmChainId,
  type EvmCurrency,
  getEvmChainById,
} from 'sushi/evm'
import { zeroAddress } from 'viem'

export interface TokenSelectorRow<TChainId extends EvmChainId> {
  account?: AddressFor<TChainId>
  currency: CurrencyFor<TChainId>
  style?: CSSProperties
  className?: string
  onSelect(currency: CurrencyFor<TChainId>): void
  balance?: Amount<CurrencyFor<TChainId>> | undefined
  showWarning: boolean
  price?: Fraction
  pin?: {
    isPinned: boolean
    onPin(): void
  }
  selected: boolean
  isBalanceLoading: boolean
  onShowInfo: () => void
}

export const TokenSelectorRow: FC<TokenSelectorRow<EvmChainId>> = memo(
  function TokenSelectorRow({
    price,
    balance,
    currency,
    style,
    className,
    onSelect,
    pin,
    selected,
    isBalanceLoading,
    showWarning,
    onShowInfo,
  }) {
    const onClick = useCallback(() => {
      onSelect(currency)
    }, [currency, onSelect])

    const onPin = useCallback(
      (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation()
        pin?.onPin()
      },
      [pin],
    )

    const showInfo = useCallback(
      (e: React.MouseEvent | React.KeyboardEvent) => {
        e.stopPropagation()
        onShowInfo()
      },
      [onShowInfo],
    )

    return (
      <div className="relative py-0.5 h-[64px]" style={style}>
        <div
          testdata-id={`token-selector-row-${
            currency.type === 'native'
              ? zeroAddress
              : currency.wrap().address.toLowerCase()
          }`}
          onClick={onClick}
          onKeyDown={onClick}
          className={classNames(
            className,
            selected ? 'bg-secondary' : '',
            `group flex items-center w-full hover:bg-muted focus:bg-accent h-full rounded-lg px-3 token-${currency?.symbol}`,
          )}
        >
          <div className="flex items-center justify-between flex-grow gap-2 rounded cursor-pointer">
            <div className="flex flex-row items-center flex-grow gap-4">
              {selected ? (
                <Badge
                  position="bottom-right"
                  badgeContent={
                    <div className="bg-white rounded-full dark:bg-slate-800 black:bg-gray-900">
                      <CheckCircleIcon
                        width={20}
                        height={20}
                        className="rounded-full text-blue"
                      />
                    </div>
                  }
                >
                  <div className="w-10 h-10">
                    <Currency.Icon
                      disableLink
                      currency={currency}
                      width={40}
                      height={40}
                    />
                  </div>
                </Badge>
              ) : (
                <div className="w-10 h-10">
                  <Currency.Icon
                    disableLink
                    currency={currency}
                    width={40}
                    height={40}
                  />
                </div>
              )}
              <div className="flex flex-col items-start">
                <div className="flex gap-1">
                  <span className="font-semibold text-primary">
                    {currency.symbol}
                  </span>
                  {showWarning ? (
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <ExclamationCircleIcon
                            width={20}
                            height={20}
                            className="text-yellow"
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          Not on our default token list
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : null}
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-sm text-muted-foreground hover:underline">
                        {currency.name ?? currency.symbol}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      className="flex items-center gap-1"
                    >
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href={getChainById(currency.chainId).getTokenUrl(
                          // Ugly cast but we know this is safe
                          currency.wrap().address as EvmAddress,
                        )}
                        className="text-blue hover:underline flex gap-1"
                      >
                        Show on explorer{' '}
                        <ArrowTopRightOnSquareIcon width={16} height={16} />
                      </a>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {isBalanceLoading ? (
                <div className="flex flex-col min-w-[60px]">
                  <SkeletonText className="w-[60px]" align="right" />
                  <SkeletonText
                    fontSize="sm"
                    className="w-[20px]"
                    align="right"
                  />
                </div>
              ) : (
                balance?.gt(ZERO) && (
                  <div className="flex flex-col max-w-[140px]">
                    <span
                      className={classNames(
                        selected ? 'font-semibold' : 'font-medium',
                        'text-right text-gray-900 dark:text-slate-50 truncate black:text-slate-50',
                      )}
                    >
                      {balance?.toSignificant(6)}
                    </span>
                    <span className="text-sm font-medium text-right text-gray-500 dark:text-slate-400">
                      {price
                        ? `$${balance?.mul(price).toString({ fixed: 2 })}`
                        : '-'}
                    </span>
                  </div>
                )
              )}
              {pin && (
                <IconButton
                  size="xs"
                  icon="⭐"
                  variant="ghost"
                  name="pin"
                  onClick={onPin}
                  className={classNames(
                    pin.isPinned ? '' : 'grayscale opacity-50',
                    'z-50',
                  )}
                />
              )}
              <IconButton
                size="xs"
                icon={InformationCircleIcon}
                variant="ghost"
                name="info"
                onClick={showInfo}
              />
            </div>
          </div>
        </div>
      </div>
    )
  },
)

export function TokenSelectorRowLoading() {
  return (
    <div className="block flex-1 py-0.5 h-[64px]">
      <div className="flex items-center w-full h-full px-3 rounded-lg">
        <div className="flex items-center justify-between flex-grow gap-2 rounded">
          <div className="flex flex-row items-center flex-grow gap-4">
            <SkeletonCircle radius={40} />
            <div className="flex flex-col items-start">
              <SkeletonText className="w-full w-[100px]" />
              <SkeletonText fontSize="sm" className="w-full w-[60px]" />
            </div>
          </div>

          <div className="flex flex-col w-full">
            <SkeletonText className="w-[80px]" />
            <SkeletonText fontSize="sm" align="right" className="w-[40px]" />
          </div>
        </div>
      </div>
    </div>
  )
}
